import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, Heart, Share2, Truck, RotateCcw, Shield, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { productsApi } from '../lib/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, setIsCartOpen } = useCart();
  const { isAuthenticated, login } = useAuth();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const productData = await productsApi.getById(id);
        setProduct(productData);
        setActiveImageIndex(0);
        
        // Fetch related products
        const allProducts = await productsApi.getAll({ category: productData.category });
        setRelatedProducts(allProducts.filter(p => p.id !== id).slice(0, 4));
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // Get all images (support both single image and images array)
  const productImages = product?.images?.length > 0 
    ? product.images 
    : (product?.image ? [product.image] : []);

  if (isLoading) {
    return (
      <main className="min-h-screen pt-20 bg-coconut flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent-ui" />
      </main>
    );
  }

  if (!product) {
    return (
      <main className="min-h-screen pt-20 bg-coconut flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-4">Product not found</h1>
          <Button onClick={() => navigate('/shop')} className="bg-accent-ui hover:bg-accent-ui/90 text-white">
            Back to Shop
          </Button>
        </div>
      </main>
    );
  }

  const handleAddToCart = async () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    if (!selectedColor) {
      toast.error('Please select a color');
      return;
    }
    
    setIsAddingToCart(true);
    try {
      await addToCart(product, selectedSize, selectedColor, quantity);
      toast.success(`${product.name} added to cart`);
      setIsCartOpen(true);
    } catch (error) {
      if (!isAuthenticated) {
        // For guest users, the local cart will work
        toast.success(`${product.name} added to cart`);
        setIsCartOpen(true);
      } else {
        toast.error('Failed to add to cart. Please try again.');
      }
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <main className="min-h-screen pt-20 bg-coconut">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-text-subtle hover:text-text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to shopping
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="aspect-[3/4] bg-stone-100 rounded-sm overflow-hidden">
              <img
                src={productImages[activeImageIndex] || product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Thumbnail Gallery */}
            {productImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {productImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={cn(
                      "flex-shrink-0 w-20 h-24 rounded-sm overflow-hidden border-2 transition-all",
                      activeImageIndex === index 
                        ? "border-accent-ui" 
                        : "border-transparent hover:border-stone-300"
                    )}
                  >
                    <img
                      src={img}
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:py-8">
            <div className="flex items-center gap-2 mb-4">
              {product.is_new && (
                <Badge className="bg-accent-ui text-white border-0">New</Badge>
              )}
              {product.is_sale && (
                <Badge className="bg-tangerine text-white border-0">Sale</Badge>
              )}
            </div>

            <h1 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-2">
              {product.name}
            </h1>

            <p className="text-text-subtle text-lg mb-6">
              {product.description}
            </p>

            <div className="flex items-baseline gap-3 mb-8">
              <span className="font-display text-3xl font-bold text-text-primary">
                ${product.price.toFixed(2)}
              </span>
              {product.original_price && (
                <span className="text-xl text-text-subtle line-through">
                  ${product.original_price.toFixed(2)}
                </span>
              )}
              {product.original_price && (
                <Badge variant="outline" className="border-tangerine text-tangerine">
                  {Math.round((1 - product.price / product.original_price) * 100)}% OFF
                </Badge>
              )}
            </div>

            {/* Color Selection */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-text-primary mb-3 uppercase tracking-wider">
                Color: {selectedColor || 'Select a color'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={cn(
                      "px-4 py-2 text-sm border rounded-sm transition-all",
                      selectedColor === color
                        ? "border-accent-ui bg-accent-ui/5 text-text-primary"
                        : "border-stone-300 text-text-subtle hover:border-stone-400"
                    )}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Size Selection */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider">
                  Size: {selectedSize || 'Select a size'}
                </h3>
                <button className="text-sm text-accent-ui hover:underline">
                  Size Guide
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={cn(
                      "min-w-[48px] h-12 px-4 text-sm border rounded-sm transition-all",
                      selectedSize === size
                        ? "border-accent-ui bg-accent-ui text-white"
                        : "border-stone-300 text-text-subtle hover:border-stone-400"
                    )}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-text-primary mb-3 uppercase tracking-wider">
                Quantity
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-stone-300 rounded-sm">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 text-text-subtle hover:text-text-primary transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 text-text-subtle hover:text-text-primary transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-sm text-text-subtle">
                  {product.stock} items available
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mb-8">
              <Button
                onClick={handleAddToCart}
                disabled={isAddingToCart}
                className="flex-1 h-14 bg-accent-ui hover:bg-accent-ui/90 text-white text-base"
              >
                {isAddingToCart ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Add to Cart'
                )}
              </Button>
              <Button variant="outline" size="icon" className="h-14 w-14 border-stone-300">
                <Heart className="h-5 w-5" />
              </Button>
              <Button variant="outline" size="icon" className="h-14 w-14 border-stone-300">
                <Share2 className="h-5 w-5" />
              </Button>
            </div>

            {/* Features */}
            <div className="space-y-4 pt-8 border-t border-stone-200">
              <div className="flex items-center gap-3">
                <Truck className="h-5 w-5 text-accent-ui" />
                <span className="text-sm text-text-subtle">Free shipping on orders over $200</span>
              </div>
              <div className="flex items-center gap-3">
                <RotateCcw className="h-5 w-5 text-accent-ui" />
                <span className="text-sm text-text-subtle">Free 30-day returns</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-accent-ui" />
                <span className="text-sm text-text-subtle">2-year warranty included</span>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-24">
            <h2 className="font-display text-2xl font-bold text-text-primary mb-8">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default ProductDetailPage;
